import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogBody } from './ui/dialog';
import { Loader2 } from 'lucide-react';

const CATEGORY_LABELS = { case_notes: 'Case Note', email: 'Email', sms: 'Text', prompts: 'AI Prompt' };

export const QuickNoteGroupDialog = ({ open, onOpenChange, group, caseTypes, onSave, saving }) => {
  const [name, setName] = useState('');
  const [caseTypeId, setCaseTypeId] = useState('');
  const [allowRunAll, setAllowRunAll] = useState(false);

  useEffect(() => {
    if (open) {
      setName(group?.name || '');
      setCaseTypeId(group?.case_type_id || '');
      setAllowRunAll(!!group?.allow_run_all);
    }
  }, [open, group]);

  const isBuiltin = group?.is_builtin;
  const submit = () => {
    const ct = caseTypes.find(c => c.id === caseTypeId);
    onSave({ name, case_type_id: caseTypeId, case_type_name: ct?.name, allow_run_all: allowRunAll });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{group ? 'Edit Quick Note Group' : 'New Quick Note Group'}</DialogTitle>
          <DialogDescription>Quick notes in this group appear on cases of the selected case type.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label>Group Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SNAP E&T Intake" data-testid="quick-note-group-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Case Type *</Label>
            {isBuiltin ? (
              <p className="text-sm text-muted-foreground" data-testid="quick-note-group-builtin-case-type">{group.case_type_name} (built-in, can't be changed)</p>
            ) : (
              <Select value={caseTypeId} onValueChange={setCaseTypeId}>
                <SelectTrigger data-testid="quick-note-group-case-type-select"><SelectValue placeholder="Select a case type" /></SelectTrigger>
                <SelectContent>
                  {caseTypes.map(ct => <SelectItem key={ct.id} value={ct.id}>{ct.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-start justify-between gap-3 rounded-md border p-3">
            <div>
              <Label htmlFor="allow-run-all-toggle">Allow Run All</Label>
              <p className="text-xs text-muted-foreground">
                Adds a "Run All" option on the case page that runs every quick note in this group at once
                {isBuiltin ? ' (only quick notes added here in Settings, not the original built-in ones)' : ''}.
              </p>
            </div>
            <Switch id="allow-run-all-toggle" checked={allowRunAll} onCheckedChange={setAllowRunAll} data-testid="quick-note-group-allow-run-all-toggle" />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !name.trim() || (!isBuiltin && !caseTypeId)} data-testid="save-quick-note-group-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save Group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const QuickNoteDialog = ({ open, onOpenChange, note, templates, onSave, saving }) => {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setName(note?.name || '');
      setSelected(note?.template_ids || []);
      setSearch('');
    }
  }, [open, note]);

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const filtered = templates.filter(t => (t.name || '').toLowerCase().includes(search.toLowerCase()));
  const isBuiltin = note?.is_builtin;
  const nameOf = (id) => templates.find(t => t.id === id)?.name || id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{note ? 'Edit Quick Note' : 'Add Quick Note'}</DialogTitle>
          <DialogDescription>Each assigned template creates its own case note when this quick note is used.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label>Quick Note Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Intake & Enrollment" data-testid="quick-note-name-input" />
          </div>
          {isBuiltin && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2" data-testid="quick-note-builtin-info">
              {selected.length === 0
                ? `Currently uses its built-in content. ${note.default_description || ''}`
                : 'Assigned templates will replace the built-in content. Untick all templates to go back to the built-in content.'}
            </div>
          )}
          <div className="space-y-2">
            <Label>Message Templates {isBuiltin ? '' : '*'} ({selected.length} selected, notes created in this order)</Label>
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1" data-testid="quick-note-selected-templates">
                {selected.map((id, i) => <Badge key={id} variant="secondary">{i + 1}. {nameOf(id)}</Badge>)}
              </div>
            )}
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search templates..." data-testid="quick-note-template-search" />
            <div className="max-h-64 overflow-y-auto border rounded-md p-2 space-y-1 bg-gray-50">
              {filtered.map(t => (
                <label key={t.id} className="flex items-center gap-2 text-sm p-1 rounded hover:bg-white cursor-pointer">
                  <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`quick-note-template-option-${t.id}`} />
                  <span className="flex-1 truncate">{t.name}</span>
                  <Badge variant="outline" className="text-[10px]">{CATEGORY_LABELS[t.category] || t.category}</Badge>
                </label>
              ))}
              {filtered.length === 0 && <p className="text-xs text-muted-foreground p-2">No templates found</p>}
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave({ name, template_ids: selected })} disabled={saving || !name.trim() || (!isBuiltin && selected.length === 0)} data-testid="save-quick-note-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save Quick Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
